/**
 * Commerce (Order) — catalog & product management.
 */
import { createSlice } from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import { endPoints } from "../../api/endpoints";
import { formatAxiosErrorMessage } from "../../utils/common";

const buildEndpoint = (template, params = {}) =>
    Object.entries(params).reduce(
        (acc, [k, v]) => acc.replaceAll(`{{${k}}}`, v),
        template,
    );

// Last products list query — mutations refetch with the same filters.
let lastProductListQuery = { catalog_id: null, filters: {} };

const initialAsync = { isLoading: false, data: null, error: null };
const unwrapApiData = (payload) => payload?.data ?? payload;
const unwrapShippingPayload = (payload) => {
    const data = unwrapApiData(payload);
    return data?.shipping ?? data;
};
const unwrapTaxPayload = (payload) => {
    const data = unwrapApiData(payload);
    return data?.tax ?? data;
};
const unwrapDiscountPayload = (payload) => {
    const data = unwrapApiData(payload);
    return data?.discount ?? data;
};
const unwrapOrderPayload = (payload) => {
    if (!payload) return payload;
    if (payload.id) return payload;
    if (payload.data?.id) return payload.data;
    if (payload.data?.data?.id) return payload.data.data;
    return payload;
};
const toList = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.data?.results)) return raw.data.results;
    return [];
};

const normalizeCatalogStatusKey = (value) => String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
const catalogStatusAliasKey = (value) => ({
    success: "paid",
    captured: "paid",
    failure: "failed",
    cancelled: "failed",
    canceled: "failed",
    expired: "failed",
    refund: "refund_initiated",
    refund_pending: "refund_initiated",
    pending_refund: "refund_initiated",
    refund_created: "refund_initiated",
    processed: "refunded",
    partial_refund: "partially_refunded",
    failed_refund: "refund_failed",
}[normalizeCatalogStatusKey(value)] || normalizeCatalogStatusKey(value));

const dedupeCatalogStatuses = (items) => {
    const seen = new Set();
    return [...items]
        .sort((a, b) => {
            const typeCompare = String(a?.type || "").localeCompare(String(b?.type || ""));
            if (typeCompare !== 0) return typeCompare;
            const priorityCompare = Number(a?.priority || 0) - Number(b?.priority || 0);
            if (priorityCompare !== 0) return priorityCompare;
            return String(a?.created_on || "").localeCompare(String(b?.created_on || ""));
        })
        .filter((item) => {
            const key = `${item?.type || ""}:${catalogStatusAliasKey(item?.status)}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const sortCatalogStatuses = (items) => dedupeCatalogStatuses(items).sort((a, b) => {
    const typeCompare = String(a?.type || "").localeCompare(String(b?.type || ""));
    if (typeCompare !== 0) return typeCompare;
    return Number(a?.priority || 0) - Number(b?.priority || 0);
});

const CommerceInitialState = {
    orderList: { ...initialAsync },
    orderUpdate: { ...initialAsync },
    orderAnalytics: { ...initialAsync },

    catalogList: { ...initialAsync },
    catalogCreate: { ...initialAsync },
    catalogUpdate: { ...initialAsync },
    catalogDelete: { ...initialAsync },
    catalogConnect: { ...initialAsync },
    globalSettings: { ...initialAsync },
    globalSettingsUpdate: { ...initialAsync },
    catalogPushToFb: { ...initialAsync },
    catalogSyncFromFb: { ...initialAsync },
    catalogSyncAllFromFb: { ...initialAsync },
    businessList: { ...initialAsync },
    catalogStatusList: { ...initialAsync },
    catalogStatusCreate: { ...initialAsync },
    catalogStatusUpdate: { ...initialAsync },
    catalogStatusDelete: { ...initialAsync },
    catalogStatusPatch: { ...initialAsync },
    productCategoryList: { ...initialAsync },
    productCategoryCreate: { ...initialAsync },
    productCategoryUpdate: { ...initialAsync },
    productCategoryDelete: { ...initialAsync },
    shippingUpdate: { ...initialAsync },
    shippingStateOptions: { ...initialAsync },
    taxUpdate: { ...initialAsync },
    taxStateOptions: { ...initialAsync },
    discountUpdate: { ...initialAsync },

    productList: { ...initialAsync },
    productCreate: { ...initialAsync },
    productUpdate: { ...initialAsync },
    productDelete: { ...initialAsync },
    productImport: { ...initialAsync },
};

const replaceOrderInCollection = (collection, order) => {
    if (!order?.id || !collection) return collection;
    const mergeOrder = (item) => (item.id === order.id ? { ...item, ...order } : item);

    if (Array.isArray(collection)) {
        return collection.map(mergeOrder);
    }
    if (Array.isArray(collection.results)) {
        return {
            ...collection,
            results: collection.results.map(mergeOrder),
        };
    }
    if (Array.isArray(collection.data)) {
        return {
            ...collection,
            data: collection.data.map(mergeOrder),
        };
    }
    if (Array.isArray(collection.data?.results)) {
        return {
            ...collection,
            data: {
                ...collection.data,
                results: collection.data.results.map(mergeOrder),
            },
        };
    }
    return collection;
};

export const CommerceSlice = createSlice({
    name: "commerce",
    initialState: { ...CommerceInitialState },
    reducers: {
        // ---- Order list ----
        OrderListRequesting: (state) => { state.orderList.isLoading = true; state.orderList.error = null; },
        OrderListSuccess: (state, action) => { state.orderList.isLoading = false; state.orderList.data = action.payload; },
        OrderListError: (state, action) => { state.orderList.isLoading = false; state.orderList.error = action.payload; },
        OrderListReset: (state) => { state.orderList = { ...initialAsync }; },
        OrderUpdateRequesting: (state) => { state.orderUpdate.isLoading = true; state.orderUpdate.error = null; },
        OrderUpdateSuccess: (state, action) => {
            const updatedOrder = unwrapOrderPayload(action.payload);
            state.orderUpdate.isLoading = false;
            state.orderUpdate.error = null;
            state.orderUpdate.data = updatedOrder;
            state.orderList.data = replaceOrderInCollection(state.orderList.data, updatedOrder);
        },
        OrderUpdateError: (state, action) => { state.orderUpdate.isLoading = false; state.orderUpdate.error = action.payload; },
        OrderUpdateReset: (state) => { state.orderUpdate = { ...initialAsync }; },
        OrderAnalyticsRequesting: (state) => { state.orderAnalytics.isLoading = true; state.orderAnalytics.error = null; },
        OrderAnalyticsSuccess: (state, action) => {
            state.orderAnalytics.isLoading = false;
            state.orderAnalytics.error = null;
            state.orderAnalytics.data = unwrapApiData(action.payload);
        },
        OrderAnalyticsError: (state, action) => { state.orderAnalytics.isLoading = false; state.orderAnalytics.error = action.payload; },
        OrderAnalyticsReset: (state) => { state.orderAnalytics = { ...initialAsync }; },

        // ---- Catalog list ----
        CatalogListRequesting: (state) => { state.catalogList.isLoading = true; state.catalogList.error = null; },
        CatalogListSuccess: (state, action) => { state.catalogList.isLoading = false; state.catalogList.data = action.payload; },
        CatalogListError: (state, action) => { state.catalogList.isLoading = false; state.catalogList.error = action.payload; },
        CatalogListReset: (state) => { state.catalogList = { ...initialAsync }; },

        // ---- Catalog create ----
        CatalogCreateRequesting: (state) => { state.catalogCreate.isLoading = true; state.catalogCreate.error = null; },
        CatalogCreateSuccess: (state, action) => { state.catalogCreate.isLoading = false; state.catalogCreate.data = action.payload; },
        CatalogCreateError: (state, action) => { state.catalogCreate.isLoading = false; state.catalogCreate.error = action.payload; },
        CatalogCreateReset: (state) => { state.catalogCreate = { ...initialAsync }; },

        // ---- Catalog update ----
        CatalogUpdateRequesting: (state) => { state.catalogUpdate.isLoading = true; state.catalogUpdate.error = null; },
        CatalogUpdateSuccess: (state, action) => { state.catalogUpdate.isLoading = false; state.catalogUpdate.data = action.payload; },
        CatalogUpdateError: (state, action) => { state.catalogUpdate.isLoading = false; state.catalogUpdate.error = action.payload; },
        CatalogUpdateReset: (state) => { state.catalogUpdate = { ...initialAsync }; },

        // ---- Catalog delete ----
        CatalogDeleteRequesting: (state) => { state.catalogDelete.isLoading = true; state.catalogDelete.error = null; },
        CatalogDeleteSuccess: (state) => { state.catalogDelete.isLoading = false; state.catalogDelete.data = "deleted"; },
        CatalogDeleteError: (state, action) => { state.catalogDelete.isLoading = false; state.catalogDelete.error = action.payload; },
        CatalogDeleteReset: (state) => { state.catalogDelete = { ...initialAsync }; },

        // ---- Catalog connect to WABA ----
        CatalogConnectRequesting: (state) => { state.catalogConnect.isLoading = true; state.catalogConnect.error = null; },
        CatalogConnectSuccess: (state, action) => { state.catalogConnect.isLoading = false; state.catalogConnect.data = action.payload; },
        CatalogConnectError: (state, action) => { state.catalogConnect.isLoading = false; state.catalogConnect.error = action.payload; },
        CatalogConnectReset: (state) => { state.catalogConnect = { ...initialAsync }; },

        // ---- Catalog global settings ----
        GlobalSettingsRequesting: (state) => { state.globalSettings.isLoading = true; state.globalSettings.error = null; },
        GlobalSettingsSuccess: (state, action) => {
            state.globalSettings.isLoading = false;
            state.globalSettings.error = null;
            state.globalSettings.data = unwrapApiData(action.payload);
        },
        GlobalSettingsError: (state, action) => { state.globalSettings.isLoading = false; state.globalSettings.error = action.payload; },
        GlobalSettingsReset: (state) => { state.globalSettings = { ...initialAsync }; },
        GlobalSettingsUpdateRequesting: (state) => { state.globalSettingsUpdate.isLoading = true; state.globalSettingsUpdate.error = null; },
        GlobalSettingsUpdateSuccess: (state, action) => {
            state.globalSettingsUpdate.isLoading = false;
            state.globalSettingsUpdate.error = null;
            state.globalSettingsUpdate.data = unwrapApiData(action.payload);
        },
        GlobalSettingsUpdateError: (state, action) => { state.globalSettingsUpdate.isLoading = false; state.globalSettingsUpdate.error = action.payload; },
        GlobalSettingsUpdateReset: (state) => { state.globalSettingsUpdate = { ...initialAsync }; },

        // ---- Catalog push to FB ----
        CatalogPushToFbRequesting: (state) => { state.catalogPushToFb.isLoading = true; state.catalogPushToFb.error = null; },
        CatalogPushToFbSuccess: (state, action) => { state.catalogPushToFb.isLoading = false; state.catalogPushToFb.data = action.payload; },
        CatalogPushToFbError: (state, action) => { state.catalogPushToFb.isLoading = false; state.catalogPushToFb.error = action.payload; },
        CatalogPushToFbReset: (state) => { state.catalogPushToFb = { ...initialAsync }; },

        // ---- Catalog sync from FB ----
        CatalogSyncFromFbRequesting: (state) => { state.catalogSyncFromFb.isLoading = true; state.catalogSyncFromFb.error = null; },
        CatalogSyncFromFbSuccess: (state, action) => { state.catalogSyncFromFb.isLoading = false; state.catalogSyncFromFb.data = action.payload; },
        CatalogSyncFromFbError: (state, action) => { state.catalogSyncFromFb.isLoading = false; state.catalogSyncFromFb.error = action.payload; },
        CatalogSyncFromFbReset: (state) => { state.catalogSyncFromFb = { ...initialAsync }; },

        // ---- Catalog sync ALL from FB (catalogs + products) ----
        CatalogSyncAllFromFbRequesting: (state) => { state.catalogSyncAllFromFb.isLoading = true; state.catalogSyncAllFromFb.error = null; },
        CatalogSyncAllFromFbSuccess: (state, action) => { state.catalogSyncAllFromFb.isLoading = false; state.catalogSyncAllFromFb.data = action.payload; },
        CatalogSyncAllFromFbError: (state, action) => { state.catalogSyncAllFromFb.isLoading = false; state.catalogSyncAllFromFb.error = action.payload; },
        CatalogSyncAllFromFbReset: (state) => { state.catalogSyncAllFromFb = { ...initialAsync }; },

        // ---- Business list ----
        BusinessListRequesting: (state) => { state.businessList.isLoading = true; state.businessList.error = null; },
        BusinessListSuccess: (state, action) => { state.businessList.isLoading = false; state.businessList.data = action.payload; },
        BusinessListError: (state, action) => { state.businessList.isLoading = false; state.businessList.error = action.payload; },

        // ---- Catalog statuses ----
        CatalogStatusListRequesting: (state) => { state.catalogStatusList.isLoading = true; state.catalogStatusList.error = null; },
        CatalogStatusListSuccess: (state, action) => {
            state.catalogStatusList.isLoading = false;
            state.catalogStatusList.error = null;
            const incomingType = action.payload?.type || "";
            const incoming = toList(action.payload?.data ?? action.payload);
            if (incomingType) {
                const existing = toList(state.catalogStatusList.data);
                state.catalogStatusList.data = sortCatalogStatuses([
                    ...existing.filter((item) => item.type !== incomingType),
                    ...incoming,
                ]);
                return;
            }
            state.catalogStatusList.data = sortCatalogStatuses(incoming);
        },
        CatalogStatusListError: (state, action) => { state.catalogStatusList.isLoading = false; state.catalogStatusList.error = action.payload; },
        CatalogStatusListReset: (state) => { state.catalogStatusList = { ...initialAsync }; },
        CatalogStatusCreateRequesting: (state) => { state.catalogStatusCreate.isLoading = true; state.catalogStatusCreate.error = null; },
        CatalogStatusCreateSuccess: (state, action) => { state.catalogStatusCreate.isLoading = false; state.catalogStatusCreate.data = action.payload; },
        CatalogStatusCreateError: (state, action) => { state.catalogStatusCreate.isLoading = false; state.catalogStatusCreate.error = action.payload; },
        CatalogStatusCreateReset: (state) => { state.catalogStatusCreate = { ...initialAsync }; },
        CatalogStatusUpdateRequesting: (state) => { state.catalogStatusUpdate.isLoading = true; state.catalogStatusUpdate.error = null; },
        CatalogStatusUpdateSuccess: (state, action) => { state.catalogStatusUpdate.isLoading = false; state.catalogStatusUpdate.data = action.payload; },
        CatalogStatusUpdateError: (state, action) => { state.catalogStatusUpdate.isLoading = false; state.catalogStatusUpdate.error = action.payload; },
        CatalogStatusUpdateReset: (state) => { state.catalogStatusUpdate = { ...initialAsync }; },
        CatalogStatusDeleteRequesting: (state) => { state.catalogStatusDelete.isLoading = true; state.catalogStatusDelete.error = null; },
        CatalogStatusDeleteSuccess: (state, action) => { state.catalogStatusDelete.isLoading = false; state.catalogStatusDelete.data = action.payload ?? "deleted"; },
        CatalogStatusDeleteError: (state, action) => { state.catalogStatusDelete.isLoading = false; state.catalogStatusDelete.error = action.payload; },
        CatalogStatusDeleteReset: (state) => { state.catalogStatusDelete = { ...initialAsync }; },
        CatalogStatusPatchRequesting: (state) => { state.catalogStatusPatch.isLoading = true; state.catalogStatusPatch.error = null; },
        CatalogStatusPatchSuccess: (state, action) => { state.catalogStatusPatch.isLoading = false; state.catalogStatusPatch.data = action.payload; },
        CatalogStatusPatchError: (state, action) => { state.catalogStatusPatch.isLoading = false; state.catalogStatusPatch.error = action.payload; },
        CatalogStatusPatchReset: (state) => { state.catalogStatusPatch = { ...initialAsync }; },
        ProductCategoryListRequesting: (state) => { state.productCategoryList.isLoading = true; state.productCategoryList.error = null; },
        ProductCategoryListSuccess: (state, action) => {
            state.productCategoryList.isLoading = false;
            state.productCategoryList.data = action.payload;
        },
        ProductCategoryListError: (state, action) => { state.productCategoryList.isLoading = false; state.productCategoryList.error = action.payload; },
        ProductCategoryListReset: (state) => { state.productCategoryList = { ...initialAsync }; },
        ProductCategoryCreateRequesting: (state) => { state.productCategoryCreate.isLoading = true; state.productCategoryCreate.error = null; },
        ProductCategoryCreateSuccess: (state, action) => { state.productCategoryCreate.isLoading = false; state.productCategoryCreate.data = action.payload; },
        ProductCategoryCreateError: (state, action) => { state.productCategoryCreate.isLoading = false; state.productCategoryCreate.error = action.payload; },
        ProductCategoryCreateReset: (state) => { state.productCategoryCreate = { ...initialAsync }; },
        ProductCategoryUpdateRequesting: (state) => { state.productCategoryUpdate.isLoading = true; state.productCategoryUpdate.error = null; },
        ProductCategoryUpdateSuccess: (state, action) => { state.productCategoryUpdate.isLoading = false; state.productCategoryUpdate.data = action.payload; },
        ProductCategoryUpdateError: (state, action) => { state.productCategoryUpdate.isLoading = false; state.productCategoryUpdate.error = action.payload; },
        ProductCategoryUpdateReset: (state) => { state.productCategoryUpdate = { ...initialAsync }; },
        ProductCategoryDeleteRequesting: (state) => { state.productCategoryDelete.isLoading = true; state.productCategoryDelete.error = null; },
        ProductCategoryDeleteSuccess: (state, action) => { state.productCategoryDelete.isLoading = false; state.productCategoryDelete.data = action.payload ?? "deleted"; },
        ProductCategoryDeleteError: (state, action) => { state.productCategoryDelete.isLoading = false; state.productCategoryDelete.error = action.payload; },
        ProductCategoryDeleteReset: (state) => { state.productCategoryDelete = { ...initialAsync }; },
        ShippingUpdateRequesting: (state) => { state.shippingUpdate.isLoading = true; state.shippingUpdate.error = null; },
        ShippingUpdateSuccess: (state, action) => {
            const shipping = unwrapShippingPayload(action.payload);
            state.shippingUpdate.isLoading = false;
            state.shippingUpdate.error = null;
            state.shippingUpdate.data = shipping;
            if (state.globalSettings.data) {
                state.globalSettings.data = {
                    ...state.globalSettings.data,
                    shipping,
                };
            }
        },
        ShippingUpdateError: (state, action) => { state.shippingUpdate.isLoading = false; state.shippingUpdate.error = action.payload; },
        ShippingUpdateReset: (state) => { state.shippingUpdate = { ...initialAsync }; },
        ShippingStateOptionsRequesting: (state) => { state.shippingStateOptions.isLoading = true; state.shippingStateOptions.error = null; },
        ShippingStateOptionsSuccess: (state, action) => {
            state.shippingStateOptions.isLoading = false;
            state.shippingStateOptions.error = null;
            state.shippingStateOptions.data = unwrapApiData(action.payload);
        },
        ShippingStateOptionsError: (state, action) => { state.shippingStateOptions.isLoading = false; state.shippingStateOptions.error = action.payload; },
        ShippingStateOptionsReset: (state) => { state.shippingStateOptions = { ...initialAsync }; },
        TaxUpdateRequesting: (state) => { state.taxUpdate.isLoading = true; state.taxUpdate.error = null; },
        TaxUpdateSuccess: (state, action) => {
            const tax = unwrapTaxPayload(action.payload);
            state.taxUpdate.isLoading = false;
            state.taxUpdate.error = null;
            state.taxUpdate.data = tax;
            if (state.globalSettings.data) {
                state.globalSettings.data = {
                    ...state.globalSettings.data,
                    tax,
                };
            }
        },
        TaxUpdateError: (state, action) => { state.taxUpdate.isLoading = false; state.taxUpdate.error = action.payload; },
        TaxUpdateReset: (state) => { state.taxUpdate = { ...initialAsync }; },
        TaxStateOptionsRequesting: (state) => { state.taxStateOptions.isLoading = true; state.taxStateOptions.error = null; },
        TaxStateOptionsSuccess: (state, action) => {
            state.taxStateOptions.isLoading = false;
            state.taxStateOptions.error = null;
            state.taxStateOptions.data = unwrapApiData(action.payload);
        },
        TaxStateOptionsError: (state, action) => { state.taxStateOptions.isLoading = false; state.taxStateOptions.error = action.payload; },
        TaxStateOptionsReset: (state) => { state.taxStateOptions = { ...initialAsync }; },
        DiscountUpdateRequesting: (state) => { state.discountUpdate.isLoading = true; state.discountUpdate.error = null; },
        DiscountUpdateSuccess: (state, action) => {
            const discount = unwrapDiscountPayload(action.payload);
            state.discountUpdate.isLoading = false;
            state.discountUpdate.error = null;
            state.discountUpdate.data = discount;
            if (state.globalSettings.data) {
                state.globalSettings.data = {
                    ...state.globalSettings.data,
                    discount,
                };
            }
        },
        DiscountUpdateError: (state, action) => { state.discountUpdate.isLoading = false; state.discountUpdate.error = action.payload; },
        DiscountUpdateReset: (state) => { state.discountUpdate = { ...initialAsync }; },

        // ---- Product list ----
        ProductListRequesting: (state) => { state.productList.isLoading = true; state.productList.error = null; },
        ProductListSuccess: (state, action) => { state.productList.isLoading = false; state.productList.data = action.payload; },
        ProductListError: (state, action) => { state.productList.isLoading = false; state.productList.error = action.payload; },
        ProductListReset: (state) => { state.productList = { ...initialAsync }; },

        // ---- Product create ----
        ProductCreateRequesting: (state) => { state.productCreate.isLoading = true; state.productCreate.error = null; },
        ProductCreateSuccess: (state, action) => { state.productCreate.isLoading = false; state.productCreate.data = action.payload; },
        ProductCreateError: (state, action) => { state.productCreate.isLoading = false; state.productCreate.error = action.payload; },
        ProductCreateReset: (state) => { state.productCreate = { ...initialAsync }; },

        // ---- Product update ----
        ProductUpdateRequesting: (state) => { state.productUpdate.isLoading = true; state.productUpdate.error = null; },
        ProductUpdateSuccess: (state, action) => { state.productUpdate.isLoading = false; state.productUpdate.data = action.payload; },
        ProductUpdateError: (state, action) => { state.productUpdate.isLoading = false; state.productUpdate.error = action.payload; },
        ProductUpdateReset: (state) => { state.productUpdate = { ...initialAsync }; },

        // ---- Product delete ----
        ProductDeleteRequesting: (state) => { state.productDelete.isLoading = true; state.productDelete.error = null; },
        ProductDeleteSuccess: (state) => { state.productDelete.isLoading = false; state.productDelete.data = "deleted"; },
        ProductDeleteError: (state, action) => { state.productDelete.isLoading = false; state.productDelete.error = action.payload; },
        ProductDeleteReset: (state) => { state.productDelete = { ...initialAsync }; },

        // ---- Product CSV import ----
        ProductImportRequesting: (state) => { state.productImport.isLoading = true; state.productImport.error = null; },
        ProductImportSuccess: (state, action) => { state.productImport.isLoading = false; state.productImport.data = action.payload; },
        ProductImportError: (state, action) => { state.productImport.isLoading = false; state.productImport.error = action.payload; },
        ProductImportReset: (state) => { state.productImport = { ...initialAsync }; },
    },
});

export const {
    OrderListRequesting, OrderListSuccess, OrderListError, OrderListReset,
    OrderUpdateRequesting, OrderUpdateSuccess, OrderUpdateError, OrderUpdateReset,
    OrderAnalyticsRequesting, OrderAnalyticsSuccess, OrderAnalyticsError, OrderAnalyticsReset,
    CatalogListRequesting, CatalogListSuccess, CatalogListError, CatalogListReset,
    CatalogCreateRequesting, CatalogCreateSuccess, CatalogCreateError, CatalogCreateReset,
    CatalogUpdateRequesting, CatalogUpdateSuccess, CatalogUpdateError, CatalogUpdateReset,
    CatalogDeleteRequesting, CatalogDeleteSuccess, CatalogDeleteError, CatalogDeleteReset,
    CatalogConnectRequesting, CatalogConnectSuccess, CatalogConnectError, CatalogConnectReset,
    GlobalSettingsRequesting, GlobalSettingsSuccess, GlobalSettingsError, GlobalSettingsReset,
    GlobalSettingsUpdateRequesting, GlobalSettingsUpdateSuccess, GlobalSettingsUpdateError, GlobalSettingsUpdateReset,
    CatalogPushToFbRequesting, CatalogPushToFbSuccess, CatalogPushToFbError, CatalogPushToFbReset,
    CatalogSyncFromFbRequesting, CatalogSyncFromFbSuccess, CatalogSyncFromFbError, CatalogSyncFromFbReset,
    CatalogSyncAllFromFbRequesting, CatalogSyncAllFromFbSuccess, CatalogSyncAllFromFbError, CatalogSyncAllFromFbReset,
    BusinessListRequesting, BusinessListSuccess, BusinessListError,
    CatalogStatusListRequesting, CatalogStatusListSuccess, CatalogStatusListError, CatalogStatusListReset,
    CatalogStatusCreateRequesting, CatalogStatusCreateSuccess, CatalogStatusCreateError, CatalogStatusCreateReset,
    CatalogStatusUpdateRequesting, CatalogStatusUpdateSuccess, CatalogStatusUpdateError, CatalogStatusUpdateReset,
    CatalogStatusDeleteRequesting, CatalogStatusDeleteSuccess, CatalogStatusDeleteError, CatalogStatusDeleteReset,
    CatalogStatusPatchRequesting, CatalogStatusPatchSuccess, CatalogStatusPatchError, CatalogStatusPatchReset,
    ProductCategoryListRequesting, ProductCategoryListSuccess, ProductCategoryListError, ProductCategoryListReset,
    ProductCategoryCreateRequesting, ProductCategoryCreateSuccess, ProductCategoryCreateError, ProductCategoryCreateReset,
    ProductCategoryUpdateRequesting, ProductCategoryUpdateSuccess, ProductCategoryUpdateError, ProductCategoryUpdateReset,
    ProductCategoryDeleteRequesting, ProductCategoryDeleteSuccess, ProductCategoryDeleteError, ProductCategoryDeleteReset,
    ShippingUpdateRequesting, ShippingUpdateSuccess, ShippingUpdateError, ShippingUpdateReset,
    ShippingStateOptionsRequesting, ShippingStateOptionsSuccess, ShippingStateOptionsError, ShippingStateOptionsReset,
    TaxUpdateRequesting, TaxUpdateSuccess, TaxUpdateError, TaxUpdateReset,
    TaxStateOptionsRequesting, TaxStateOptionsSuccess, TaxStateOptionsError, TaxStateOptionsReset,
    DiscountUpdateRequesting, DiscountUpdateSuccess, DiscountUpdateError, DiscountUpdateReset,
    ProductListRequesting, ProductListSuccess, ProductListError, ProductListReset,
    ProductCreateRequesting, ProductCreateSuccess, ProductCreateError, ProductCreateReset,
    ProductUpdateRequesting, ProductUpdateSuccess, ProductUpdateError, ProductUpdateReset,
    ProductDeleteRequesting, ProductDeleteSuccess, ProductDeleteError, ProductDeleteReset,
    ProductImportRequesting, ProductImportSuccess, ProductImportError, ProductImportReset,
} = CommerceSlice.actions;

export const CommerceState = (state) => state.commerce;
export default CommerceSlice.reducer;

// ---- Thunks ----

export const fetchOrdersAPI = (query = "") => async (dispatch) => {
    dispatch(OrderListRequesting());
    try {
        const response = await apiService.get(`${endPoints.commerce.orders}${query}`);
        dispatch(OrderListSuccess(response.data));
    } catch (error) {
        dispatch(OrderListError(formatAxiosErrorMessage(error)));
    }
};

export const fetchOrderAnalyticsAPI = (query = "") => async (dispatch) => {
    dispatch(OrderAnalyticsRequesting());
    try {
        const response = await apiService.get(`${endPoints.commerce.orderAnalytics}${query}`);
        dispatch(OrderAnalyticsSuccess(response.data));
    } catch (error) {
        dispatch(OrderAnalyticsError(formatAxiosErrorMessage(error)));
    }
};

export const updateOrderStatusesAPI = (id, payload) => async (dispatch) => {
    dispatch(OrderUpdateRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.orderStatusesUpdate, { id }),
            payload,
        );
        dispatch(OrderUpdateSuccess(response.data));
    } catch (error) {
        dispatch(OrderUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const downloadCommerceOrderInvoiceAPI = (id) => async () => {
    try {
        const response = await apiService.get(
            buildEndpoint(endPoints.commerce.orderInvoice, { id }),
            { responseType: "blob" },
        );
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
        const filename = filenameMatch ? filenameMatch[1] : "order-invoice.pdf";
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        return filename;
    } catch (error) {
        throw new Error(formatAxiosErrorMessage(error));
    }
};

export const fetchCatalogsAPI = () => async (dispatch) => {
    dispatch(CatalogListRequesting());
    try {
        const response = await apiService.get(endPoints.commerce.catalogs);
        dispatch(CatalogListSuccess(response.data));
    } catch (error) {
        dispatch(CatalogListError(formatAxiosErrorMessage(error)));
    }
};

export const createCatalogAPI = (payload) => async (dispatch) => {
    dispatch(CatalogCreateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.catalogs, payload);
        dispatch(CatalogCreateSuccess(response.data));
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogCreateError(formatAxiosErrorMessage(error)));
    }
};

export const updateCatalogAPI = (id, payload) => async (dispatch) => {
    dispatch(CatalogUpdateRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.catalog, { id }),
            payload,
        );
        dispatch(CatalogUpdateSuccess(response.data));
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const deleteCatalogAPI = (id) => async (dispatch) => {
    dispatch(CatalogDeleteRequesting());
    try {
        await apiService.delete(buildEndpoint(endPoints.commerce.catalog, { id }));
        dispatch(CatalogDeleteSuccess());
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogDeleteError(formatAxiosErrorMessage(error)));
    }
};

export const connectCatalogToWabaAPI = (id) => async (dispatch) => {
    dispatch(CatalogConnectRequesting());
    try {
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.catalogConnectWaba, { id }),
            {},
        );
        dispatch(CatalogConnectSuccess(response.data));
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogConnectError(formatAxiosErrorMessage(error)));
    }
};

export const fetchCommerceGlobalSettingsAPI = () => async (dispatch) => {
    dispatch(GlobalSettingsRequesting());
    try {
        const response = await apiService.get(endPoints.commerce.catalogGlobalSettings);
        dispatch(GlobalSettingsSuccess(response.data));
    } catch (error) {
        dispatch(GlobalSettingsError(formatAxiosErrorMessage(error)));
    }
};

export const updateCommerceGlobalSettingsAPI = (payload) => async (dispatch) => {
    dispatch(GlobalSettingsUpdateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.catalogGlobalSettings, payload);
        dispatch(GlobalSettingsUpdateSuccess(response.data));
        dispatch(GlobalSettingsSuccess(response.data));
    } catch (error) {
        dispatch(GlobalSettingsUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const pushCatalogToFbAPI = (id, payload = {}) => async (dispatch) => {
    dispatch(CatalogPushToFbRequesting());
    try {
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.catalogPushToFb, { id }),
            payload,
        );
        dispatch(CatalogPushToFbSuccess(response.data));
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogPushToFbError(formatAxiosErrorMessage(error)));
    }
};

export const syncAllCatalogsFromFbAPI = (remove_missing = true) => async (dispatch) => {
    dispatch(CatalogSyncAllFromFbRequesting());
    try {
        const response = await apiService.post(
            endPoints.commerce.catalogSyncAllFromFb,
            { remove_missing },
        );
        dispatch(CatalogSyncAllFromFbSuccess(response.data));
        dispatch(fetchCatalogsAPI());
    } catch (error) {
        dispatch(CatalogSyncAllFromFbError(formatAxiosErrorMessage(error)));
    }
};

export const syncCatalogFromFbAPI = (id) => async (dispatch) => {
    dispatch(CatalogSyncFromFbRequesting());
    try {
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.catalogSyncFromFb, { id }),
            {},
        );
        dispatch(CatalogSyncFromFbSuccess(response.data));
    } catch (error) {
        dispatch(CatalogSyncFromFbError(formatAxiosErrorMessage(error)));
    }
};

export const fetchBusinessesAPI = () => async (dispatch) => {
    dispatch(BusinessListRequesting());
    try {
        const response = await apiService.get(endPoints.commerce.catalogBusinesses);
        dispatch(BusinessListSuccess(response.data));
    } catch (error) {
        dispatch(BusinessListError(formatAxiosErrorMessage(error)));
    }
};

export const fetchCatalogStatusesAPI = (type = "") => async (dispatch) => {
    dispatch(CatalogStatusListRequesting());
    try {
        const query = type ? `?type=${type}` : "";
        const response = await apiService.get(`${endPoints.commerce.catalogStatuses}${query}`);
        dispatch(CatalogStatusListSuccess({ data: response.data, type }));
    } catch (error) {
        dispatch(CatalogStatusListError(formatAxiosErrorMessage(error)));
    }
};

export const createCatalogStatusAPI = (payload, type = "") => async (dispatch) => {
    dispatch(CatalogStatusCreateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.catalogStatuses, payload);
        dispatch(CatalogStatusCreateSuccess(response.data));
        dispatch(fetchCatalogStatusesAPI(type || payload?.type || ""));
    } catch (error) {
        dispatch(CatalogStatusCreateError(formatAxiosErrorMessage(error)));
    }
};

export const updateCatalogStatusAPI = (id, payload, type = "") => async (dispatch) => {
    dispatch(CatalogStatusUpdateRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.catalogStatus, { id }),
            payload,
        );
        dispatch(CatalogStatusUpdateSuccess(response.data));
        dispatch(fetchCatalogStatusesAPI(type || payload?.type || ""));
    } catch (error) {
        dispatch(CatalogStatusUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const deleteCatalogStatusAPI = (id, payload = {}, type = "") => async (dispatch) => {
    dispatch(CatalogStatusDeleteRequesting());
    try {
        const response = await apiService.delete(
            buildEndpoint(endPoints.commerce.catalogStatus, { id }),
            { data: payload },
        );
        dispatch(CatalogStatusDeleteSuccess(response.data));
        dispatch(fetchCatalogStatusesAPI(type));
    } catch (error) {
        dispatch(CatalogStatusDeleteError(formatAxiosErrorMessage(error)));
    }
};

export const updateCatalogStatusPriorityAPI = (payload, type = "") => async (dispatch) => {
    dispatch(CatalogStatusUpdateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.catalogStatusBulk, payload);
        dispatch(CatalogStatusUpdateSuccess(response.data));
        dispatch(fetchCatalogStatusesAPI(type));
    } catch (error) {
        dispatch(CatalogStatusUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const patchCatalogStatusAPI = (id, payload, type = "") => async (dispatch) => {
    dispatch(CatalogStatusPatchRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.catalogStatus, { id }),
            payload,
        );
        dispatch(CatalogStatusPatchSuccess(response.data));
        dispatch(fetchCatalogStatusesAPI(type || payload?.type || ""));
    } catch (error) {
        dispatch(CatalogStatusPatchError(formatAxiosErrorMessage(error)));
    }
};

export const fetchProductCategoriesAPI = () => async (dispatch) => {
    dispatch(ProductCategoryListRequesting());
    try {
        const response = await apiService.get(endPoints.commerce.productCategories);
        dispatch(ProductCategoryListSuccess(response.data));
    } catch (error) {
        dispatch(ProductCategoryListError(formatAxiosErrorMessage(error)));
    }
};

export const createProductCategoryAPI = (payload) => async (dispatch) => {
    dispatch(ProductCategoryCreateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.productCategories, payload);
        dispatch(ProductCategoryCreateSuccess(response.data));
        dispatch(fetchProductCategoriesAPI());
    } catch (error) {
        dispatch(ProductCategoryCreateError(formatAxiosErrorMessage(error)));
    }
};

export const updateProductCategoryAPI = (id, payload) => async (dispatch) => {
    dispatch(ProductCategoryUpdateRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.productCategory, { id }),
            payload,
        );
        dispatch(ProductCategoryUpdateSuccess(response.data));
        dispatch(fetchProductCategoriesAPI());
    } catch (error) {
        dispatch(ProductCategoryUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const deleteProductCategoryAPI = (id) => async (dispatch) => {
    dispatch(ProductCategoryDeleteRequesting());
    try {
        const response = await apiService.delete(
            buildEndpoint(endPoints.commerce.productCategory, { id }),
        );
        dispatch(ProductCategoryDeleteSuccess(response.data));
        dispatch(fetchProductCategoriesAPI());
    } catch (error) {
        dispatch(ProductCategoryDeleteError(formatAxiosErrorMessage(error)));
    }
};

export const updateCommerceShippingSettingsAPI = (payload) => async (dispatch) => {
    dispatch(ShippingUpdateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.shipping, payload);
        dispatch(ShippingUpdateSuccess(response.data));
    } catch (error) {
        dispatch(ShippingUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const fetchShippingStateOptionsAPI = (query = "", include = []) => async (dispatch) => {
    dispatch(ShippingStateOptionsRequesting());
    try {
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        include.filter(Boolean).forEach((id) => params.append("include", id));
        const response = await apiService.get(`${endPoints.commerce.shippingStateOptions}?${params.toString()}`);
        dispatch(ShippingStateOptionsSuccess(response.data));
    } catch (error) {
        dispatch(ShippingStateOptionsError(formatAxiosErrorMessage(error)));
    }
};

export const updateCommerceTaxSettingsAPI = (payload) => async (dispatch) => {
    dispatch(TaxUpdateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.tax, payload);
        dispatch(TaxUpdateSuccess(response.data));
    } catch (error) {
        dispatch(TaxUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const fetchTaxStateOptionsAPI = (query = "", include = []) => async (dispatch) => {
    dispatch(TaxStateOptionsRequesting());
    try {
        const params = new URLSearchParams();
        if (query) params.set("q", query);
        include.filter(Boolean).forEach((id) => params.append("include", id));
        const response = await apiService.get(`${endPoints.commerce.taxStateOptions}?${params.toString()}`);
        dispatch(TaxStateOptionsSuccess(response.data));
    } catch (error) {
        dispatch(TaxStateOptionsError(formatAxiosErrorMessage(error)));
    }
};

export const updateCommerceDiscountSettingsAPI = (payload) => async (dispatch) => {
    dispatch(DiscountUpdateRequesting());
    try {
        const response = await apiService.post(endPoints.commerce.discount, payload);
        dispatch(DiscountUpdateSuccess(response.data));
    } catch (error) {
        dispatch(DiscountUpdateError(formatAxiosErrorMessage(error)));
    }
};

export const fetchProductsAPI = (catalog_id, filters) => async (dispatch) => {
    // Omit the second arg after create/update/delete so the current
    // search/category query is reused instead of dumping the user back
    // to an unfiltered list.
    const resolvedFilters = filters !== undefined
        ? (filters || {})
        : (lastProductListQuery.catalog_id === catalog_id ? lastProductListQuery.filters : {});
    lastProductListQuery = { catalog_id, filters: resolvedFilters };

    dispatch(ProductListRequesting());
    try {
        const params = new URLSearchParams();
        // Do not send `q` — the products API only honors `search`.
        const search = String(resolvedFilters.search || "").trim();
        const availability = String(resolvedFilters.availability || "").trim();
        const page = Number(resolvedFilters.page || 1);
        if (search) params.set("search", search);
        if (resolvedFilters.category) params.set("category", resolvedFilters.category);
        // Exact enum: "in stock" | "out of stock" | "available for order" | "discontinued"
        if (availability) params.set("availability", availability);
        if (page) params.set("page", String(page));
        // Encode spaces as %20 so availability=in%20stock matches the API.
        const qs = params.toString().replace(/\+/g, "%20");
        const url = `${buildEndpoint(endPoints.commerce.products, { catalog_id })}${qs ? `?${qs}` : ""}`;
        const response = await apiService.get(url);
        dispatch(ProductListSuccess(response.data));
    } catch (error) {
        dispatch(ProductListError(formatAxiosErrorMessage(error)));
    }
};

export const createProductAPI = (catalog_id, payload) => async (dispatch) => {
    dispatch(ProductCreateRequesting());
    try {
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.products, { catalog_id }),
            payload,
        );
        dispatch(ProductCreateSuccess(response.data));
        dispatch(fetchProductsAPI(catalog_id));
    } catch (error) {
        dispatch(ProductCreateError(formatAxiosErrorMessage(error)));
    }
};

export const updateProductAPI = (catalog_id, id, payload) => async (dispatch) => {
    dispatch(ProductUpdateRequesting());
    try {
        const response = await apiService.patch(
            buildEndpoint(endPoints.commerce.product, { catalog_id, id }),
            payload,
        );
        dispatch(ProductUpdateSuccess(response.data));
        dispatch(fetchProductsAPI(catalog_id));
        return response.data;
    } catch (error) {
        dispatch(ProductUpdateError(formatAxiosErrorMessage(error)));
        return null;
    }
};

export const deleteProductAPI = (catalog_id, id) => async (dispatch) => {
    dispatch(ProductDeleteRequesting());
    try {
        await apiService.delete(buildEndpoint(endPoints.commerce.product, { catalog_id, id }));
        dispatch(ProductDeleteSuccess());
        dispatch(fetchProductsAPI(catalog_id));
    } catch (error) {
        dispatch(ProductDeleteError(formatAxiosErrorMessage(error)));
    }
};

export const setProductVariantGroupAPI = (catalog_id, id, item_group_id) => async (dispatch) => {
    dispatch(ProductUpdateRequesting());
    try {
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.productSetVariantGroup, { catalog_id, id }),
            { item_group_id },
        );
        dispatch(ProductUpdateSuccess(response.data));
        dispatch(fetchProductsAPI(catalog_id));
        return response.data;
    } catch (error) {
        dispatch(ProductUpdateError(formatAxiosErrorMessage(error)));
        return null;
    }
};

export const importProductsCsvAPI = (catalog_id, file) => async (dispatch) => {
    dispatch(ProductImportRequesting());
    try {
        const form = new FormData();
        form.append("file", file);
        const response = await apiService.post(
            buildEndpoint(endPoints.commerce.productsImportCsv, { catalog_id }),
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
        dispatch(ProductImportSuccess(response.data));
        dispatch(fetchProductsAPI(catalog_id));
    } catch (error) {
        dispatch(ProductImportError(formatAxiosErrorMessage(error)));
    }
};
